import { Tour } from "antd";
import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { GUIDE_HINTS } from "./hintRegistry";

const FIRST_LOOK_MS = 400;
const RETRY_MS = 300;
const MAX_ATTEMPTS = 20;

const isVisible = element => Boolean(element?.getClientRects?.().length);

const resolveSteps = guideKey =>
  (GUIDE_HINTS[guideKey] || [])
    .map((entry, index) => {
      const target = Array.from(document.querySelectorAll(entry.target)).find(isVisible);
      if (!target) {
        return null;
      }
      return {
        title: entry.title,
        description: entry.description,
        target: () => target,
        placement: "bottom",
        key: `${guideKey}-${index}`,
      };
    })
    .filter(Boolean);

/**
 * Step-by-step tips pointing at parts of the screen.
 *
 * Without props it follows the `?guide=` link from the setup guide. With
 * `guideKey` it runs that tip set directly (first-visit tips) and calls
 * `onDone` when the person closes it.
 *
 * @param {{guideKey?: string, onDone?: function()}} props
 */
export function GuideHint({ guideKey: forcedKey, onDone } = {}) {
  const [searchParams, setSearchParams] = useSearchParams();
  const guideKey = forcedKey || searchParams.get("guide");
  const [steps, setSteps] = useState([]);

  // Targets often render after data loads (or in a wizard panel that opens
  // later), so keep looking for a few seconds instead of checking once.
  useEffect(() => {
    setSteps([]);
    if (!guideKey || !GUIDE_HINTS[guideKey]) {
      return undefined;
    }
    let attempts = 0;
    let timer;
    const look = () => {
      const found = resolveSteps(guideKey);
      if (found.length) {
        setSteps(found);
        return;
      }
      attempts += 1;
      if (attempts < MAX_ATTEMPTS) {
        timer = window.setTimeout(look, RETRY_MS);
      }
    };
    timer = window.setTimeout(look, FIRST_LOOK_MS);
    return () => window.clearTimeout(timer);
  }, [guideKey]);

  const closeTour = () => {
    setSteps([]);
    if (forcedKey) {
      onDone?.();
      return;
    }
    if (!guideKey) {
      return;
    }
    const next = new URLSearchParams(searchParams);
    next.delete("guide");
    setSearchParams(next, { replace: true });
  };

  if (!steps.length) {
    return null;
  }

  return <Tour open onClose={closeTour} steps={steps} mask={{ color: "rgba(14, 14, 44, 0.45)" }} />;
}
